import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import { useState } from "react";
import { axiosInstance } from "../../../../utils/networking";
import { BroadcastFilter } from "../broadcastTypes";
import bfcList from "../../../../assets/bfc";
import cities from "../../../../assets/city";

const PRIMARY_INDUSTRY = [
  "Accommodations - Bed and breakfast, or any other lodging facility",
  "Business Services",
  "Construction and Maintenance Services - Grading, excavating, other construction and maintenance services",
  "Engineering, Design and Development",
  "Entertainment and Recreation",
  "Food",
  "Healthcare - chiropractors, dental, health clinics",
  "Maintenance & Repair - automobile repair, automobile dealership, cellular phone repair, landscaping and lawn maintenance",
  "Nonprofit - churches, religious centers, advocacy organizations, etc.",
  "Other",
  "Manufacturing",
  "Personal Services",
  "Professional & Other Services - taxes, consulting, publishing, marketing",
  "Retail",
  "Technology",
  "Transportation - limousine services, etc.",
];

const CLASSIFICATIONS = [
  "Woman-Owned",
  "Minority-Owned",
  "Veteran-Owned",
  "Disabled-Owned",
  "LGBTQ+ Owned",
  "Youth Owned",
  "Low to Moderate Income Owned",
  "Non-English Business",
  "NEP",
];

const CERTIFICATIONS = [
  "Certified Women Owned",
  "Certified Minority Owned",
  "Certified Disabled Owned",
  "Certified Veteran Owned",
  "Certified LGBTQ+ Owned",
];

interface RecipientFilterProps {
  filter: BroadcastFilter;
  setFilter: (f: BroadcastFilter) => void;
  recipientCount: number | null;
  setRecipientCount: (n: number | null) => void;
}

const RecipientFilter = ({
  filter,
  setFilter,
  recipientCount,
  setRecipientCount,
}: RecipientFilterProps) => {
  const [previewing, setPreviewing] = useState(false);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await axiosInstance.post("/broadcast/preview", { filter });
      setRecipientCount(res.data.recipientCount);
    } catch (err) {
      console.error("Preview error:", err);
    } finally {
      setPreviewing(false);
    }
  };

  const toggleArrayItem = (key: keyof BroadcastFilter, value: string) => {
    const current = (filter[key] as string[]) || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setFilter({ ...filter, [key]: updated });
    setRecipientCount(null);
  };

  const isChecked = (key: keyof BroadcastFilter, value: string) =>
    ((filter[key] as string[]) || []).includes(value);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
      {/* BFC */}
      <FormControl fullWidth>
        <InputLabel>SBC Assignment</InputLabel>
        <Select
          value={filter.bfc || ""}
          label="SBC Assignment"
          displayEmpty
          onChange={(e) => {
            setFilter({ ...filter, bfc: e.target.value || undefined });
            setRecipientCount(null);
          }}
        >
          <MenuItem value="">
            <em>All SBCs</em>
          </MenuItem>
          {bfcList.map((bfc) => (
            <MenuItem key={bfc} value={bfc}>
              {bfc}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* City */}
      <FormControl fullWidth>
        <InputLabel>Cities</InputLabel>
        <Select
          multiple
          value={filter.city || []}
          label="Cities"
          input={<OutlinedInput label="Cities" />}
          onChange={(e) => {
            setFilter({ ...filter, city: e.target.value as string[] });
            setRecipientCount(null);
          }}
          renderValue={(selected) => (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {(selected as string[]).map((v) => (
                <Chip key={v} label={v} size="small" />
              ))}
            </Stack>
          )}
        >
          {cities.map((city) => (
            <MenuItem key={city} value={city}>
              {city}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Business Type */}
      <FormControl fullWidth>
        <InputLabel>Primary Industry</InputLabel>
        <Select
          multiple
          value={filter.bussType || []}
          label="Primary Industry"
          input={<OutlinedInput label="Primary Industry" />}
          onChange={(e) => {
            setFilter({ ...filter, bussType: e.target.value as string[] });
            setRecipientCount(null);
          }}
          renderValue={(selected) => (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {(selected as string[]).map((v) => (
                <Chip key={v} label={v} size="small" />
              ))}
            </Stack>
          )}
        >
          {PRIMARY_INDUSTRY.map((bt) => (
            <MenuItem key={bt} value={bt}>
              {bt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Classification */}
      <Box>
        <FormLabel component="legend">
          <Typography variant="body2" fontWeight={600}>
            Classification
          </Typography>
        </FormLabel>
        <FormGroup row>
          {CLASSIFICATIONS.map((c) => (
            <FormControlLabel
              key={c}
              control={
                <Checkbox
                  size="small"
                  checked={isChecked("classification", c)}
                  onChange={() => toggleArrayItem("classification", c)}
                />
              }
              label={<Typography variant="body2">{c}</Typography>}
            />
          ))}
        </FormGroup>
      </Box>

      {/* Certification */}
      <Box>
        <FormLabel component="legend">
          <Typography variant="body2" fontWeight={600}>
            Certification
          </Typography>
        </FormLabel>
        <FormGroup row>
          {CERTIFICATIONS.map((c) => (
            <FormControlLabel
              key={c}
              control={
                <Checkbox
                  size="small"
                  checked={isChecked("certification", c)}
                  onChange={() => toggleArrayItem("certification", c)}
                />
              }
              label={<Typography variant="body2">{c}</Typography>}
            />
          ))}
        </FormGroup>
      </Box>

      {/* Last Contact */}
      <FormControl fullWidth>
        <InputLabel>Last Contact</InputLabel>
        <Select
          value={filter.lastContact || ""}
          label="Last Contact"
          displayEmpty
          onChange={(e) => {
            setFilter({ ...filter, lastContact: e.target.value || undefined });
            setRecipientCount(null);
          }}
        >
          <MenuItem value="">
            <em>Any time</em>
          </MenuItem>
          <MenuItem value="2weeks">Contacted within 2 weeks</MenuItem>
          <MenuItem value="1month">Contacted within 1 month</MenuItem>
          <MenuItem value="6months_plus">Not contacted in 6+ months</MenuItem>
          <MenuItem value="1year_plus">Not contacted in 1+ year</MenuItem>
        </Select>
      </FormControl>

      {/* Preview button + count */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="outlined"
          startIcon={
            previewing ? <CircularProgress size={16} /> : <PeopleIcon />
          }
          onClick={handlePreview}
          disabled={previewing}
          sx={{ borderColor: "#9d2362", color: "#9d2362" }}
        >
          Preview Recipients
        </Button>
        {recipientCount !== null && (
          <Alert
            severity={recipientCount === 0 ? "warning" : "info"}
            sx={{ py: 0.5 }}
          >
            {recipientCount === 0
              ? "No firms match the current filter. Adjust your criteria."
              : `This broadcast will reach approximately ${recipientCount.toLocaleString()} recipients.`}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default RecipientFilter;
